import React, { useMemo, useState } from 'react';
import {
  MuiBox,
  Button,
  MuiTable,
  Typography,
  SvgIcon,
  SvgIconButton,
  Card,
  Box,
  Tooltip,
  Switch
} from '@kis-technologies/kis-ui';
import { useStyles } from '../styled';
import { Product } from '../types';
import { TicketTemplate } from 'store/ticketTemplates';
import { formatKisFee } from '../utils';

interface ProductsTableProps {
  products: Product[];
  templates: TicketTemplate[];
  onCreate: () => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onToggle: (product: Product) => void;
  permissions: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    toggle: boolean;
  };
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  templates,
  onCreate,
  onEdit,
  onDelete,
  onToggle,
  permissions
}) => {
  const canCreate = permissions.create;
  const canDelete = permissions.delete;
  const canToggle = permissions.toggle;
  const classes = useStyles();

  const [sortParams, setSortParams] = useState<{ column: string; order: 'asc' | 'desc' }>({
    column: 'name',
    order: 'asc'
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sort = (data: any[], column: string, order: string) => {
    return [...data].sort((a, b) => {
      let valA = a[column] ?? '';
      let valB = b[column] ?? '';

      if (typeof valA === 'string' && typeof valB === 'string') {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getTemplateName = (templateId: string | null) => {
    if (!templateId) return '--';
    const template = templates.find((t) => t.id === templateId);
    return template ? template.name : '--';
  };

  const mappedProducts = products.map((product) => ({
    ...product,
    templateName: getTemplateName(product.ticketTemplateId),
    priceTypesCount: product.priceTypes?.length || 0,
    kisFeeValue: product.kisFeeAmount ?? product.kisFeePercent ?? 0
  }));

  const sortedProducts = useMemo(() => {
    return sort(mappedProducts, sortParams.column, sortParams.order);
  }, [mappedProducts, sortParams]);

  const handleHeaderClick = (column: string) => {
    setSortParams((prev) => ({
      column,
      order: prev.column === column && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortArrow = () => (
    <SvgIcon name={sortParams.order === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: 5 }} />
  );

  return (
    <MuiBox>
      <MuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontWeight="bold">Products</Typography>

        {canCreate && <Button onClick={onCreate}>Create Product</Button>}
      </MuiBox>

      <MuiBox className={classes.outerBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer className={classes.tableContainer}>
              <MuiTable.Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      onClick={() => handleHeaderClick('name')}
                      className={classes.tableHeaders}
                      sx={{ width: '25%' }}
                    >
                      Name {sortParams.column === 'name' && <SortArrow />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      onClick={() => handleHeaderClick('templateName')}
                      className={classes.tableHeaders}
                      sx={{ width: '25%' }}
                    >
                      Template {sortParams.column === 'templateName' && <SortArrow />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.tableHeaders}
                      align="center"
                      onClick={() => handleHeaderClick('priceTypesCount')}
                      sx={{ width: '15%' }}
                    >
                      Price Types {sortParams.column === 'priceTypesCount' && <SortArrow />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.tableHeaders}
                      align="center"
                      onClick={() => handleHeaderClick('kisFeeValue')}
                      sx={{ width: '12%', whiteSpace: 'nowrap' }}
                    >
                      KIS Fee {sortParams.column === 'kisFeeValue' && <SortArrow />}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.tableHeaders} align="center" sx={{ width: '10%' }}>
                      Enabled
                    </MuiTable.TableCell>
                    <MuiTable.TableCell className={classes.tableHeaders} align="center" sx={{ width: '14%' }}>
                      Actions
                    </MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>

                <MuiTable.TableBody>
                  {sortedProducts.map((product) => {
                    return (
                      <React.Fragment key={product.id}>
                        {/* PRODUCT ROW */}
                        <MuiTable.TableRow>
                          <MuiTable.TableCell>
                            <MuiBox display="flex" alignItems="center" gap={1}>
                              {product.name}
                            </MuiBox>
                          </MuiTable.TableCell>

                          <MuiTable.TableCell> {getTemplateName(product.ticketTemplateId)}</MuiTable.TableCell>

                          <MuiTable.TableCell align="center">{product.priceTypes?.length || 0}</MuiTable.TableCell>

                          <MuiTable.TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            {formatKisFee(product.kisFeeAmount, product.kisFeePercent)}
                          </MuiTable.TableCell>

                          <MuiTable.TableCell align="center">
                            <Switch
                              checked={product.enabled}
                              onChange={() => onToggle(product)}
                              disabled={!canToggle}
                            />
                          </MuiTable.TableCell>

                          <MuiTable.TableCell align="center">
                            <MuiBox display="flex" gap={1} justifyContent="center">
                              <Tooltip title="Edit Product">
                                <SvgIconButton onClick={() => onEdit(product)}>
                                  <SvgIcon name="edit_circle" />
                                </SvgIconButton>
                              </Tooltip>
                              {canDelete && (
                                <Tooltip title="Delete Product">
                                  <SvgIconButton onClick={() => onDelete(product)}>
                                    <SvgIcon name="exit_circle" />
                                  </SvgIconButton>
                                </Tooltip>
                              )}
                            </MuiBox>
                          </MuiTable.TableCell>
                        </MuiTable.TableRow>
                      </React.Fragment>
                    );
                  })}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
      </MuiBox>
    </MuiBox>
  );
};

export default ProductsTable;
